import { useEffect, useRef } from 'react';

import { logger } from '@azure/storage-blob';

import { calculateDistance } from '@/utils/location';

export interface FullMoveCallbackParams {
  latitude: number;
  longitude: number;
  distance: number;
  accuracy: number;
  altitude: number | null;
  altitudeAccuracy: number | null;
  heading: number | null;
  speed: number | null;
  timestamp: string;
}

export type MoveCallback = (params: FullMoveCallbackParams) => void;

export interface Coordinates {
  latitude: number;
  longitude: number;
}

const STATIONARY_INTERVAL_MS = 4 * 60 * 1000;

export const useGeolocationTracker = (
  onMoveCallback: MoveCallback,
  minDistanceMeters = 10,
): void => {
  const lastPositionRef = useRef<GeolocationPosition | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const triggerCallback = (position: GeolocationPosition, force = false) => {
    const {
      latitude,
      longitude,
      accuracy,
      altitude,
      altitudeAccuracy,
      heading,
      speed,
    } = position.coords;

    const timestampMs = position.timestamp;
    const timestamp = new Date(timestampMs).toISOString();
    const currentCoords = { latitude, longitude };

    let distance = 0;
    if (lastPositionRef.current) {
      const lastCoords = {
        latitude: lastPositionRef.current.coords.latitude,
        longitude: lastPositionRef.current.coords.longitude,
      };
      distance = calculateDistance(lastCoords, currentCoords);
    }

    if (force || distance >= minDistanceMeters) {
      logger.info(
        `Sending ${
          force ? 'forced' : 'movement'
        } update. Distance: ${distance.toFixed(2)}m`,
      );

      onMoveCallback({
        latitude,
        longitude,
        distance,
        accuracy,
        altitude,
        altitudeAccuracy,
        heading,
        speed,
        timestamp,
      });

      lastPositionRef.current = position;
    } else {
      logger.info(
        `Skipped update. Distance too small: ${distance.toFixed(2)}m`,
      );
    }
  };

  useEffect(() => {
    if (!navigator.geolocation) {
      logger.error('Geolocation is not supported by this browser.');
      return;
    }

    // Watch movement updates
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const isFirstUpdate = !lastPositionRef.current;
        triggerCallback(position, isFirstUpdate);
      },
      (error) => {
        logger.error('Geolocation error:', error);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 1000,
        timeout: 15000,
      },
    );

    // Force a fresh GPS read every 2 minutes
    intervalRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          triggerCallback(position, true);
        },
        (error) => {
          logger.error('Forced GPS read failed:', error);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 15000,
        },
      );
    }, STATIONARY_INTERVAL_MS);

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [onMoveCallback, minDistanceMeters]);
};
